import { FormLabel } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Trash2, Loader2 } from 'lucide-react';
import type { AssignmentPermissionsSectionProps } from './UserDialogTypes';

export function AssignmentPermissionsSection({
  form: _form,
  isSubmittingForm,
  isEdit,
  assignments,
  assignmentsLoading,
  assignmentPermissions,
  permissionsLoading,
  emailPreferencesLoading,
  syncingPermissions,
  syncProgress,
  canEditAssignmentPermissions,
  canEditEmailNotifications,
  addAssignmentPermission,
  removeAssignmentPermission,
  updateAssignmentPermissionLevel,
  updateEmailNotificationPreference,
  t
}: AssignmentPermissionsSectionProps) {
  // Get assignments that are not already assigned to this user
  const availableAssignments = assignments.filter(
    assignment => !assignmentPermissions.find(ap => ap.assignmentId === assignment.id)
  );

  // Show loading indicators for various states
  const isLoadingAnyData = assignmentsLoading || permissionsLoading || emailPreferencesLoading;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FormLabel className="text-base font-medium">
            {t('usersActionDialog.labels.assignmentPermissions')}
          </FormLabel>
          {isLoadingAnyData && (
            <>
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {assignmentsLoading && t('usersActionDialog.loading.assignments')}
                {permissionsLoading && t('usersActionDialog.loading.permissions')}
                {emailPreferencesLoading && t('usersActionDialog.loading.emailPreferences')}
              </span>
            </>
          )}
        </div>

        {/* Add Assignment Permission Dropdown */}
        {canEditAssignmentPermissions && (
          assignmentsLoading ? (
            <Skeleton className="h-10 w-48" />
          ) : availableAssignments.length > 0 ? (
            <Select
              onValueChange={(assignmentId) => {
                addAssignmentPermission(assignmentId);
              }}
              disabled={isSubmittingForm}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder={t('common.select')} />
              </SelectTrigger>
              <SelectContent>
                {availableAssignments.map((assignment) => (
                  <SelectItem key={assignment.id} value={assignment.id}>
                    {assignment.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null
        )}
      </div>

      {/* Assignment Permissions List */}
      <div className="space-y-3">
        {permissionsLoading && isEdit ? (
          // Show skeleton cards while loading permissions for edit mode - more cards for better visual balance
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded-lg p-4 space-y-3 bg-gray-50 transition-opacity duration-300 ease-in-out opacity-75">
                <div className="flex items-center justify-between">
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-10 w-32" />
                    <Skeleton className="h-8 w-8 rounded" />
                  </div>
                </div>
                <div className="flex items-center justify-between pl-2 border-t pt-2 mt-2">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-5 w-10 rounded-sm" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : assignmentPermissions.length === 0 ? (
          <div className="text-sm text-gray-500 py-4 text-center border-2 border-dashed border-gray-200 rounded-lg">
            {isEdit
              ? t('usersActionDialog.noAssignmentPermissions')
              : t('usersActionDialog.noAssignmentPermissionsCreate')
            }
          </div>
        ) : (
          <div className="space-y-3 transition-opacity duration-300 ease-in-out opacity-100">
            {assignmentPermissions.map((permission) => (
              <div
                key={permission.assignmentId}
                className="border rounded-lg p-4 space-y-3 bg-gray-50 transition-opacity duration-200 ease-in-out opacity-100"
                data-testid={`assignment-permission-${permission.assignmentId}`}
              >
              {/* First Row: Assignment Name and Permission Level */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">
                    {permission.assignmentName}
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    ID: {permission.assignmentId}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {canEditAssignmentPermissions ? (
                    // Editable view for admins
                    <>
                      <Select
                        value={permission.permissionLevel}
                        onValueChange={(newLevel) => updateAssignmentPermissionLevel(permission.assignmentId, newLevel)}
                        disabled={isSubmittingForm}
                      >
                        <SelectTrigger className="w-32" data-testid="permission-level-dropdown">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OWNER">{t('usersActionDialog.permissionLevels.owner')}</SelectItem>
                          <SelectItem value="EDITOR">{t('usersActionDialog.permissionLevels.editor')}</SelectItem>
                          <SelectItem value="OPERATOR">{t('usersActionDialog.permissionLevels.operator')}</SelectItem>
                          <SelectItem value="VIEWER">{t('usersActionDialog.permissionLevels.viewer')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeAssignmentPermission(permission.assignmentId)}
                        disabled={isSubmittingForm}
                        data-testid="remove-assignment-permission-button"
                        aria-label={`Remove ${permission.assignmentName} permission`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    // Read-only view for regular users
                    <span className="text-sm text-gray-600 font-medium px-3 py-1 bg-gray-100 rounded">
                      {permission.permissionLevel === 'OWNER' && t('usersActionDialog.permissionLevels.owner')}
                      {permission.permissionLevel === 'EDITOR' && t('usersActionDialog.permissionLevels.editor')}
                      {permission.permissionLevel === 'OPERATOR' && t('usersActionDialog.permissionLevels.operator')}
                      {permission.permissionLevel === 'VIEWER' && t('usersActionDialog.permissionLevels.viewer')}
                    </span>
                  )}
                </div>
              </div>

              {/* Second Row: Email Notifications */}
              <div className="flex items-center justify-between pl-2 border-t pt-2 mt-2">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={permission.emailNotifications}
                    onCheckedChange={canEditEmailNotifications ? (checked) => updateEmailNotificationPreference(permission.assignmentId, checked) : (_checked) => {}}
                    disabled={!canEditEmailNotifications || isSubmittingForm}
                    data-testid={`email-notification-toggle-${permission.assignmentId}`}
                    id={`email-notification-${permission.assignmentId}`}
                  />
                  <label
                    htmlFor={`email-notification-${permission.assignmentId}`}
                    className="text-sm text-gray-700 font-medium cursor-pointer"
                  >
                    {t('usersActionDialog.emailNotifications')}
                  </label>
                </div>
                <span className="text-xs text-gray-500">
                  {t('usersActionDialog.emailNotificationsHelp')}
                </span>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {/* Progress indicator for permission sync */}
      {syncingPermissions && (
        <div className="px-4 py-3 border rounded-lg bg-blue-50 text-blue-700">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
            <span className="text-sm font-medium">{syncProgress}</span>
          </div>
        </div>
      )}

      {/* Helper text */}
      {!isEdit && (
        <p className="text-xs text-gray-500 mt-2">
          {t('usersActionDialog.assignmentPermissionsHelp')}
        </p>
      )}
    </div>
  );
}